/**
 * Générateur — Définir l'opposé et la valeur absolue d'un nombre.
 * Attendu ID : 703 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 1)
 *
 * Plan de cartes (validé — le PIVOT du cluster : a + b = 0, automatisme B2) :
 *
 *   Carte « trou » — Ex.7 Addition à trou → opposé
 *     qcm_unique (a/b : QUEL CALCUL complète n + … = m, puis n + … = 0 —
 *     l'automatisme B2 exactement), texte_trou_drag (d : les 4 égalités
 *     x + {{opposé}} = 0, étiquettes signées → zéro frappe), texte (c/e :
 *     saisie de l'opposé, écriture imposée AVEC signe, y compris +).
 *
 *   Carte « tableau » — Ex.8 Tableau des opposés
 *     texte_trou_saisie + texte_trou_drag : 4 paires dont 2 « À REBOURS »
 *     (l'opposé est donné, le nombre est le trou) — l'invariant de valeur
 *     ajoutée du doc, garanti à chaque tirage.
 *
 *   Carte « double » — Ex.9 L'opposé de l'opposé
 *     texte_trou_saisie (chaînes nombre → opposé → opposé de l'opposé, dont
 *     une chaîne À REBOURS et le cas 0 en rotation), texte_trou_drag (la
 *     règle -(-a) = a adossée à un exemple tiré), qcm_unique (b : « c'est le
 *     nombre lui-même », exemple tiré pour la variabilité).
 *
 *   Carte « droite » — Ex.10 Distance à zéro
 *     Élément numberline NATIF (pas d'image) : 6 points lettrés dont 2 paires
 *     d'abscisses opposées par construction. texte_trou_drag (abscisse /
 *     signe / distance à zéro — sur un point NÉGATIF uniquement : pour un
 *     point positif, « 8 » et « +8 » seraient interchangeables → indécidable),
 *     tableau_vf, qcm_multiple (les paires opposées, compte annoncé),
 *     texte (saisie de la distance à zéro : toujours positive, zéro piège de
 *     signe).
 *
 * Vocabulaire : « distance à zéro (valeur absolue) » — jamais la notation |a|.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 703;

const L_TROU = 'De l\'addition à trou à l\'opposé';
const L_TABLEAU = 'Le tableau des opposés';
const L_DOUBLE = 'L\'opposé de l\'opposé';
const L_DROITE = 'Distance à zéro sur la droite graduée';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Dixièmes entiers → écriture française ; signe(v) impose TOUJOURS le signe.
const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signe = (d) => (d === 0 ? '0' : d < 0 ? dix(d) : `+${dix(d)}`);
// Affichage dans un énoncé : négatif entre parenthèses, positif nu.
const aff = (d) => (d < 0 ? `(${dix(d)})` : dix(d));

const CONSIGNE_SIGNE = 'Écris chaque réponse avec son signe (ex. : -5 ou +2,5).';

/** Tire un dixième non nul ; entier ou décimal selon `decimal`. */
const tirerVal = (decimal) => {
    const v = decimal
        ? (() => { let k = randInt(11, 99); if (k % 10 === 0) k += 3; return k; })()
        : randInt(2, 25) * 10;
    return randomChoice([true, false]) ? v : -v;
};

// ═════════ Carte 1 — Addition à trou → opposé (Ex.7) ═════════

const genTrouCalculQcm = () => {
    const n = randInt(3, 19);
    const typeB = randomChoice([true, false]);
    const m = typeB ? 0 : n + randInt(2, 26);
    const correct = `${m} - ${n}`;
    const distracteurs = [`${m} + ${n}`, `${n} - ${m}`, `${m} × ${n}`];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Quel calcul faut-il faire pour compléter la somme ${n} + … = ${m} ?</b></p>`,
        opts(L_TROU));
    payload._meta.params = { n, m };
    return payload;
};

const genTrouEgalitesDrag = () => {
    const p1 = randInt(2, 9) * 10;
    let p2 = randInt(11, 89);
    if (p2 % 10 === 0) p2 += 3;
    const n1 = -randInt(2, 9) * 10;
    let n2 = -(randInt(11, 89));
    if (n2 % 10 === 0) n2 -= 3;
    const vals = [p1, p2, n1, n2];
    const phrase = vals.map(v => `${aff(v)} + {{${signe(-v)}}} = 0`).join(' ; ') + '.';
    const candidats = vals.map(v => signe(v)); // le piège : oublier de changer le signe
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète chaque égalité.</b> (Rappel : deux nombres opposés ont pour somme 0.)</p>',
        { ...opts(L_TROU), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { vals };
    return payload;
};

const genTrouOpposeSaisie = () => {
    const x = tirerVal(randomChoice([true, false]));
    const payload = makeSaisie(signe(-x),
        `<p><b>Quel est l'opposé de ${aff(x)} ?</b> Écris ta réponse avec son signe (ex. : -5 ou +2,5).</p>`,
        opts(L_TROU));
    payload._meta.params = { x };
    return payload;
};

// ═════════ Carte 2 — Tableau des opposés (Ex.8) ═════════

/** 4 paires (valeurs absolues toutes distinctes), dont 2 à rebours. */
const tirerPaires = () => {
    const absolues = new Set();
    for (let essai = 0; essai < 60 && absolues.size < 4; essai++) {
        const decimal = randomChoice([true, false]);
        let a = decimal ? randInt(11, 99) : randInt(2, 25) * 10;
        if (decimal && a % 10 === 0) a += 3;
        absolues.add(a);
    }
    [70, 51, 100, 82].forEach(a => { if (absolues.size < 4) absolues.add(a); }); // repli (Ex.8 du doc)
    return [...absolues].map((a, i) => ({
        v: randomChoice([true, false]) ? a : -a,
        rebours: i >= 2, // les 2 dernières : l'opposé est donné, le nombre est le trou
    }));
};

const phraseOpposes = (paires) => shuffle(paires)
    .map(p => (p.rebours
        ? `nombre : {{${signe(p.v)}}} → son opposé : ${aff(-p.v)}`
        : `nombre : ${aff(p.v)} → son opposé : {{${signe(-p.v)}}}`))
    .join(' ; ') + '.';

const genTableauSaisie = () => {
    const paires = tirerPaires();
    const payload = makeTexteTrou(phraseOpposes(paires),
        `<p><b>Complète le tableau des opposés.</b> ${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { vals: paires.map(p => p.v) };
    return payload;
};

const genTableauDrag = () => {
    const paires = tirerPaires();
    const phrase = phraseOpposes(paires);
    const candidats = paires.flatMap(p => [signe(p.v), signe(-p.v)]); // les non-attendus survivent au filtre
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète le tableau des opposés.</b></p>',
        { ...opts(L_TABLEAU), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 4) });
    payload._meta.params = { vals: paires.map(p => p.v) };
    return payload;
};

// ═════════ Carte 3 — L'opposé de l'opposé (Ex.9) ═════════

const genDoubleChaine = () => {
    const x1 = tirerVal(true);
    let x2 = tirerVal(false);
    if (Math.abs(x2) === Math.abs(x1)) x2 = x2 > 0 ? x2 + 10 : x2 - 10; // repli déterministe
    const avecZero = randomChoice([true, false, false]);

    const segments = [
        `nombre : ${aff(x1)} → opposé : {{${signe(-x1)}}} → opposé de l'opposé : {{${signe(x1)}}}`,
        `nombre : {{${signe(x2)}}} → opposé : ${aff(-x2)} → opposé de l'opposé : {{${signe(x2)}}}`,
    ];
    if (avecZero) segments.push('nombre : 0 → opposé : {{0}} → opposé de l\'opposé : {{0}}');
    const payload = makeTexteTrou(shuffle(segments).join(' ; ') + '.',
        `<p><b>Complète les chaînes d'opposés.</b> ${CONSIGNE_SIGNE.replace('chaque réponse', 'chaque nombre non nul')}</p>`,
        { ...opts(L_DOUBLE), mode: 'saisie' });
    payload._meta.params = { x1, x2, avecZero };
    return payload;
};

const genDoubleRegleDrag = () => {
    const x = tirerVal(randomChoice([true, false]));
    const phrase = `Pour ${aff(x)} : son opposé est ${aff(-x)}, et l'opposé de son opposé est {{${signe(x)}}}. `
        + 'Pour tout nombre a : -(-a) = {{a}}.';
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète : que vaut l\'opposé de l\'opposé ?</b></p>',
        { ...opts(L_DOUBLE), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, [signe(-x), '-a', '0']) });
    payload._meta.params = { x };
    return payload;
};

const genDoubleQcm = () => {
    const x = tirerVal(randomChoice([true, false]));
    const correct = 'C\'est le nombre lui-même.';
    const payload = makeQcm(
        [correct, 'C\'est toujours un nombre négatif.', 'C\'est toujours 0.', 'C\'est le double du nombre.'],
        correct,
        `<p><b>Que peut-on dire de l'opposé de l'opposé d'un nombre relatif, par exemple de ${aff(x)} ?</b></p>`,
        opts(L_DOUBLE));
    payload._meta.params = { x };
    return payload;
};

// ═════════ Carte 4 — Distance à zéro sur la droite graduée (Ex.10) ═════════

const LETTRES_PTS = ['A', 'B', 'C', 'D', 'F', 'J'];

/** 6 points entiers dont 2 paires opposées ; abscisses toutes distinctes. */
const tirerDroite = () => {
    const p1 = randInt(2, 11);
    let p2 = randInt(2, 11);
    if (p2 === p1) p2 = p1 === 11 ? 2 : p1 + 1; // repli déterministe
    const seuls = [];
    for (let essai = 0; essai < 60 && seuls.length < 2; essai++) {
        const v = randomChoice([true, false]) ? randInt(1, 11) : -randInt(1, 11);
        if (![p1, -p1, p2, -p2, ...seuls].includes(v) && v !== 0) seuls.push(v);
    }
    for (let v = 1; v <= 11 && seuls.length < 2; v++) { // repli exhaustif
        [v, -v].forEach(w => {
            if (seuls.length < 2 && ![p1, -p1, p2, -p2, ...seuls].includes(w)) seuls.push(w);
        });
    }
    const valeurs = shuffle([p1, -p1, p2, -p2, ...seuls]);
    const lettres = shuffle(LETTRES_PTS);
    const points = valeurs.map((v, i) => ({ lettre: lettres[i], v }));
    return { points, paires: [[p1, -p1], [p2, -p2]] };
};

const elementDroite = (points) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: -12, end: 12, step: 1, reperes: [-6, 0, 6],
            points: points.map(p => ({ value: p.v, label: p.lettre, showValue: false })),
        },
    }],
});

const lettreDe = (points, v) => points.find(p => p.v === v).lettre;

const genDroiteAbscisseDrag = () => {
    const { points } = tirerDroite();
    const negatifs = points.filter(p => p.v < 0);
    const pt = randomChoice(negatifs); // point NÉGATIF : abscisse/distance discernables
    const autre = randomChoice(points.filter(p => p !== pt));
    const phrase = `Le point ${pt.lettre} a pour abscisse {{${dix(pt.v * 10)}}} ; `
        + `le signe de cette abscisse est {{-}} ; sa distance à zéro (sa valeur absolue) est {{${dix(-pt.v * 10)}}}.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Observe la droite graduée et complète.</b></p>',
        {
            ...opts(L_DROITE), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, ['+', dix(Math.abs(autre.v) * 10), dix(-Math.abs(autre.v) * 10)]).slice(0, 3),
            elements_additionnels: elementDroite(points),
        });
    payload._meta.params = { pt: pt.v };
    return payload;
};

const genDroiteVf = () => {
    const { points, paires } = tirerDroite();
    const positif = randomChoice(points.filter(p => p.v > 0));
    const quelconque = randomChoice(points);
    const distanceJuste = randomChoice([true, false]);
    const dAffichee = distanceJuste ? Math.abs(quelconque.v) : Math.abs(quelconque.v) + randInt(1, 3);
    const pairesVrai = randomChoice([true, false]);
    const [o1, o2] = paires[0];
    const nonOppose = points.find(p => p.v !== o1 && p.v !== -o1);
    const lignes = shuffle([
        [`Le point ${positif.lettre} a une abscisse positive.`, 0],
        [`Le point ${positif.lettre} a une abscisse négative.`, 1],
        [`La distance à zéro de l'abscisse du point ${quelconque.lettre} est ${dAffichee}.`, distanceJuste ? 0 : 1],
        [pairesVrai
            ? `Les abscisses des points ${lettreDe(points, o1)} et ${lettreDe(points, o2)} sont opposées.`
            : `Les abscisses des points ${lettreDe(points, o1)} et ${nonOppose.lettre} sont opposées.`,
        pairesVrai ? 0 : 1],
    ]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        '<p><b>Observe la droite graduée. Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) });
    payload._meta.params = { valeurs: points.map(p => p.v) };
    return payload;
};

const genDroiteOpposesQcmMulti = () => {
    const { points, paires } = tirerDroite();
    const corrects = paires.map(([a, b]) => `${lettreDe(points, a)} et ${lettreDe(points, b)}`);
    const faux = [];
    // parcours exhaustif mélangé des paires non opposées (borné par construction)
    const indices = shuffle(points.flatMap((_, i) => points.map((_, j) => [i, j]).filter(([a, b]) => a < b)));
    for (const [i, j] of indices) {
        if (faux.length >= 2) break;
        const x = points[i], y = points[j];
        if (x.v !== -y.v) {
            const paire = `${x.lettre} et ${y.lettre}`;
            if (!corrects.includes(paire)) faux.push(paire);
        }
    }
    const payload = makeQcm([...corrects, ...faux], corrects,
        '<p><b>Coche les deux paires de points dont les abscisses sont opposées.</b></p>',
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) }, true);
    payload._meta.params = { paires };
    return payload;
};

const genDroiteDistanceSaisie = () => {
    const { points } = tirerDroite();
    const pt = randomChoice(points);
    const payload = makeSaisie(String(Math.abs(pt.v)),
        `<p><b>Quelle est la distance à zéro (valeur absolue) de l'abscisse du point ${pt.lettre} ?</b></p>`,
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) });
    payload._meta.params = { pt: pt.v };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    trou_calcul_qcm_unique: genTrouCalculQcm,
    trou_egalites_texte_trou_drag: genTrouEgalitesDrag,
    trou_oppose_texte: genTrouOpposeSaisie,
    tableau_opposes_texte_trou_saisie: genTableauSaisie,
    tableau_opposes_texte_trou_drag: genTableauDrag,
    double_chaine_texte_trou_saisie: genDoubleChaine,
    double_regle_texte_trou_drag: genDoubleRegleDrag,
    double_qcm_unique: genDoubleQcm,
    droite_abscisse_texte_trou_drag: genDroiteAbscisseDrag,
    droite_vf: genDroiteVf,
    droite_opposes_qcm_multiple: genDroiteOpposesQcmMulti,
    droite_distance_texte: genDroiteDistanceSaisie,
};

const FORMAT_LABELS = {
    calcul_qcm_unique: 'QCM Calcul', egalites_texte_trou_drag: 'Drag',
    oppose_texte: 'Saisie', opposes_texte_trou_saisie: 'Saisie trous',
    opposes_texte_trou_drag: 'Drag', chaine_texte_trou_saisie: 'Saisie trous',
    regle_texte_trou_drag: 'Drag', qcm_unique: 'QCM',
    abscisse_texte_trou_drag: 'Drag', vf: 'Vrai/Faux',
    opposes_qcm_multiple: 'QCM Multi', distance_texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'trou', label: L_TROU, formatsList: buildFormats('trou') },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'double', label: L_DOUBLE, formatsList: buildFormats('double') },
    { id: 'droite', label: L_DROITE, formatsList: buildFormats('droite') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
